import type { GeoItemPropLike } from './geoItemProps';
/** Mobile field-note `Body` prop label (shared by web client + jobs export). */
export const FIELD_NOTE_BODY_PROP_LABEL = 'Body' as const;

/**
 * Upper bound on note body length applied at this shared read choke point, so every
 * consumer (web display + jobs exports) is bounded in one place. Set far beyond any
 * legitimate field note (~10k words) — only trips on runaway/abuse input. The
 * authoritative write-side validation lives in a separate PR.
 */
export const MAX_FIELD_NOTE_BODY_LENGTH = 50_000;

const KNOWN_SIMPLE_PROP_TYPES = new Set(['string', 'number', 'date', 'select']);

export type { GeoItemPropLike } from './geoItemProps';

/** Reads note body text from mobile-shaped geo-item props (allowlisted prop types only). */
export function extractFieldNoteBody(props: readonly GeoItemPropLike[] | null | undefined): string | undefined {
  if (!Array.isArray(props)) {
    return undefined;
  }
  const bodyProp = props.find((p) => p?.label === FIELD_NOTE_BODY_PROP_LABEL);
  if (!bodyProp || !KNOWN_SIMPLE_PROP_TYPES.has(bodyProp.type)) {
    return undefined;
  }
  return typeof bodyProp.value === 'string' ? bodyProp.value.slice(0, MAX_FIELD_NOTE_BODY_LENGTH) : undefined;
}

/**
 * Write-path guard: returns `props` with the field-note `Body` value clamped to
 * {@link MAX_FIELD_NOTE_BODY_LENGTH} so an over-long body never persists. Non-`Body`
 * props and non-string bodies pass through untouched; non-array input is returned as-is.
 */
export function clampFieldNoteBodyProps<T extends GeoItemPropLike>(
  props: readonly T[] | null | undefined,
): readonly T[] | null | undefined {
  if (!Array.isArray(props)) {
    return props;
  }
  return props.map((p) =>
    p?.label === FIELD_NOTE_BODY_PROP_LABEL &&
    typeof p.value === 'string' &&
    p.value.length > MAX_FIELD_NOTE_BODY_LENGTH
      ? { ...p, value: p.value.slice(0, MAX_FIELD_NOTE_BODY_LENGTH) }
      : p,
  );
}
